import React, { useCallback, useContext, useEffect, useMemo, useRef } from 'react';

import classNames from 'classnames';
import { useTranslation } from 'react-i18next';

import { getIframeSrc, getNameExtension, isImg } from '@shared/helpers';
import { makeDecodeURL } from '@shared/helpers/makeDecodeURL';
import { UiButton } from '@shared/ui';
import { useGetFiles } from '@widgets/Files/hooks/useGetFiles';
import { ContextFiles } from '@widgets/Files/model/context';

import { Frame } from './Frame';
import { ModalFiles } from './index';
import cls from './ModalFiles.module.css';
import { useDirectionModal } from './useDirectionModal';
import { usePrevModal } from './usePrevModal';
import { saveFile } from '../../helpers/saveFile';

interface IPrevModalProps {
    onHideModal: () => void;
    isOpen?: boolean;
}

export const PrevModal = ({ isOpen = true, onHideModal }: IPrevModalProps) => {
    const { t } = useTranslation();
    const { currentFolderId } = useContext(ContextFiles);
    const { parsedFilesData } = useGetFiles(currentFolderId);
    const { secondModal, onSecondModal, onHideSecondModal } = usePrevModal(onHideModal);
    const handleKeyDown = useCallback(
        (event: KeyboardEvent) => {
            if (event.key === 'Escape' && secondModal === null) {
                onHideModal();
            }
        },
        [secondModal, onHideModal]
    );

    useEffect(() => {
        document.addEventListener('keydown', handleKeyDown);

        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [secondModal, onHideModal, handleKeyDown]);

    const ref = useRef(null);
    const { currentFileId, onChangeCurrentFile, currentFileInfo } = useContext(ContextFiles);

    const { isLast, isFirst, onClickPrev, onClickNext } = useDirectionModal(
        ref,
        currentFileId,
        parsedFilesData,
        onChangeCurrentFile
    );

    const decodedNameFile = useMemo(() => {
        const file = parsedFilesData?.find((f) => f.id === currentFileId);
        return file ? makeDecodeURL(file.name, true) : '';
    }, [parsedFilesData, currentFileId]);

    const decodeURL = makeDecodeURL(currentFileInfo?.originalUrl || '');
    const { extension } = getNameExtension(currentFileInfo?.name || '');

    const iframeSrc = getIframeSrc(extension);

    const fileIsImg = isImg(currentFileInfo?.mimeType);

    const isPrevFormat = iframeSrc !== null || !!fileIsImg;

    return (
        isOpen && (
            <>
                <ModalFiles mode={secondModal} onHideModal={onHideSecondModal} />
                <div className={classNames(cls.customModal)}>
                    <div
                        className={classNames(cls.customModalOverlay)}
                        ref={ref}
                        onClick={onHideModal}
                    >
                        <div
                            className={cls.prevFileArrow}
                            onClick={(event) => event.stopPropagation()}
                        >
                            {!isFirst && (
                                <UiButton
                                    onClick={onClickPrev}
                                    className=""
                                    variant="icon"
                                    startIcon={
                                        <svg
                                            width="50"
                                            height="50"
                                            viewBox="0 0 50 50"
                                            fill="none"
                                            xmlns="http://www.w3.org/2000/svg"
                                        >
                                            <rect
                                                width="50"
                                                height="50"
                                                rx="25"
                                                fill="#757575"
                                                fillOpacity="0.25"
                                            />
                                            <path
                                                fillRule="evenodd"
                                                clipRule="evenodd"
                                                d="M28.3535 18.6465C28.5488 18.8417 28.5488 19.1583 28.3535 19.3536L22.7071 25L28.3535 30.6464C28.5488 30.8417 28.5488 31.1583 28.3535 31.3536C28.1583 31.5488 27.8417 31.5488 27.6464 31.3536L21.6464 25.3536C21.4512 25.1583 21.4512 24.8417 21.6464 24.6464L27.6464 18.6465C27.8417 18.4512 28.1583 18.4512 28.3535 18.6465Z"
                                                fill="#F5F5F5"
                                            />
                                        </svg>
                                    }
                                />
                            )}
                        </div>
                        <div
                            className={cls.nextFileArrow}
                            onClick={(event) => event.stopPropagation()}
                        >
                            {!isLast && (
                                <UiButton
                                    onClick={onClickNext}
                                    className=""
                                    variant="icon"
                                    startIcon={
                                        <svg
                                            width="50"
                                            height="50"
                                            viewBox="0 0 50 50"
                                            fill="none"
                                            xmlns="http://www.w3.org/2000/svg"
                                        >
                                            <rect
                                                width="50"
                                                height="50"
                                                rx="25"
                                                fill="#757575"
                                                fillOpacity="0.25"
                                            />
                                            <path
                                                fillRule="evenodd"
                                                clipRule="evenodd"
                                                d="M21.6465 31.3535C21.4512 31.1583 21.4512 30.8417 21.6465 30.6464L27.2929 25L21.6465 19.3536C21.4512 19.1583 21.4512 18.8417 21.6465 18.6464C21.8417 18.4512 22.1583 18.4512 22.3536 18.6464L28.3536 24.6464C28.5488 24.8417 28.5488 25.1583 28.3536 25.3536L22.3536 31.3535C22.1583 31.5488 21.8417 31.5488 21.6465 31.3535Z"
                                                fill="#F5F5F5"
                                            />
                                        </svg>
                                    }
                                />
                            )}
                        </div>
                        <div
                            className={cls.prevHeader}
                            onClick={(event) => event.stopPropagation()}
                        >
                            <div className={cls.iconBack}>
                                <UiButton
                                    onClick={onHideModal}
                                    className={cls.iconBackStart}
                                    variant="icon"
                                    startIcon={
                                        <svg
                                            width="32"
                                            height="32"
                                            viewBox="0 0 32 32"
                                            fill="none"
                                            xmlns="http://www.w3.org/2000/svg"
                                        >
                                            <path
                                                d="M10.375 15.375H22.875C23.0408 15.375 23.1997 15.4408 23.3169 15.5581C23.4342 15.6753 23.5 15.8342 23.5 16C23.5 16.1658 23.4342 16.3247 23.3169 16.4419C23.1997 16.5592 23.0408 16.625 22.875 16.625H10.375C10.2092 16.625 10.0503 16.5592 9.93306 16.4419C9.81585 16.3247 9.75 16.1658 9.75 16C9.75 15.8342 9.81585 15.6753 9.93306 15.5581C10.0503 15.4408 10.2092 15.375 10.375 15.375Z"
                                                fill="#F5F5F5"
                                            />
                                            <path
                                                d="M10.6336 16L15.8174 21.1825C15.9348 21.2999 16.0007 21.459 16.0007 21.625C16.0007 21.791 15.9348 21.9501 15.8174 22.0675C15.7 22.1849 15.5409 22.2508 15.3749 22.2508C15.2089 22.2508 15.0498 22.1849 14.9324 22.0675L9.3074 16.4425C9.24919 16.3844 9.20301 16.3155 9.17151 16.2395C9.14 16.1636 9.12378 16.0822 9.12378 16C9.12378 15.9178 9.14 15.8364 9.17151 15.7605C9.20301 15.6845 9.24919 15.6156 9.3074 15.5575L14.9324 9.9325C15.0498 9.81514 15.2089 9.74921 15.3749 9.74921C15.5409 9.74921 15.7 9.81514 15.8174 9.9325C15.9348 10.0499 16.0007 10.209 16.0007 10.375C16.0007 10.541 15.9348 10.7001 15.8174 10.8175L10.6336 16Z"
                                                fill="#F5F5F5"
                                            />
                                        </svg>
                                    }
                                />
                            </div>
                            <div className={cls.prevHeaderName}>{decodedNameFile}</div>
                            <div className={cls.prevHeaderIcons}>
                                <UiButton
                                    onClick={async () => {
                                        await saveFile(
                                            currentFileInfo.originalUrl,
                                            decodedNameFile
                                        );
                                    }}
                                    className=""
                                    variant="icon"
                                    startIcon={
                                        <svg
                                            width="32"
                                            height="32"
                                            viewBox="0 0 32 32"
                                            fill="none"
                                            xmlns="http://www.w3.org/2000/svg"
                                        >
                                            <path
                                                d="M6.625 18.375C6.79076 18.375 6.94973 18.4408 7.06694 18.5581C7.18415 18.6753 7.25 18.8342 7.25 19V22.125C7.25 22.4565 7.3817 22.7745 7.61612 23.0089C7.85054 23.2433 8.16848 23.375 8.5 23.375H23.5C23.8315 23.375 24.1495 23.2433 24.3839 23.0089C24.6183 22.7745 24.75 22.4565 24.75 22.125V19C24.75 18.8342 24.8158 18.6753 24.9331 18.5581C25.0503 18.4408 25.2092 18.375 25.375 18.375C25.5408 18.375 25.6997 18.4408 25.8169 18.5581C25.9342 18.6753 26 18.8342 26 19V22.125C26 22.788 25.7366 23.4239 25.2678 23.8928C24.7989 24.3616 24.163 24.625 23.5 24.625H8.5C7.83696 24.625 7.20107 24.3616 6.73223 23.8928C6.26339 23.4239 6 22.788 6 22.125V19C6 18.8342 6.06585 18.6753 6.18306 18.5581C6.30027 18.4408 6.45924 18.375 6.625 18.375Z"
                                                fill="#F5F5F5"
                                            />
                                            <path
                                                d="M15.5576 20.8175C15.6156 20.8757 15.6846 20.9219 15.7605 20.9534C15.8364 20.9849 15.9178 21.0011 16.0001 21.0011C16.0823 21.0011 16.1637 20.9849 16.2396 20.9534C16.3155 20.9219 16.3845 20.8757 16.4426 20.8175L20.1926 17.0675C20.3099 16.9501 20.3758 16.791 20.3758 16.625C20.3758 16.459 20.3099 16.2999 20.1926 16.1825C20.0752 16.0651 19.916 15.9992 19.7501 15.9992C19.5841 15.9992 19.4249 16.0651 19.3076 16.1825L16.6251 18.8663V7.875C16.6251 7.70924 16.5592 7.55027 16.442 7.43306C16.3248 7.31585 16.1658 7.25 16.0001 7.25C15.8343 7.25 15.6753 7.31585 15.5581 7.43306C15.4409 7.55027 15.3751 7.70924 15.3751 7.875V18.8663L12.6926 16.1825C12.5752 16.0651 12.416 15.9992 12.2501 15.9992C12.0841 15.9992 11.9249 16.0651 11.8076 16.1825C11.6902 16.2999 11.6243 16.459 11.6243 16.625C11.6243 16.791 11.6902 16.9501 11.8076 17.0675L15.5576 20.8175Z"
                                                fill="#F5F5F5"
                                            />
                                        </svg>
                                    }
                                />
                                <UiButton
                                    onClick={() => onSecondModal('edit')}
                                    className=""
                                    variant="icon"
                                    startIcon={
                                        <svg
                                            width="32"
                                            height="32"
                                            viewBox="0 0 32 32"
                                            fill="none"
                                            xmlns="http://www.w3.org/2000/svg"
                                        >
                                            <path
                                                d="M17.7855 8.73285C17.1964 8.79155 16.6299 9.07889 16.2182 9.58173L8.20532 19.3678L8.18767 19.5472L7.88047 22.704L7.82935 23.1653C7.80942 23.3452 7.98138 23.486 8.15384 23.431L8.59605 23.2899L11.6302 22.3659L11.8026 22.3132L19.8155 12.5272C20.6389 11.5215 20.4952 10.0793 19.4896 9.25583C18.9867 8.8441 18.3747 8.67415 17.7855 8.73285ZM17.8924 9.80571C18.176 9.77746 18.4748 9.8787 18.7769 10.1261C19.3789 10.619 19.438 11.2126 18.9452 11.8146L18.5792 12.2418L16.7419 10.7374L17.0885 10.2943C17.3359 9.99215 17.6088 9.83396 17.8924 9.80571ZM16.0293 11.6077L17.8666 13.1121L11.7381 20.5967C11.297 19.9124 10.6587 19.3898 9.90083 19.0923L16.0293 11.6077ZM9.27084 20.0787C9.97294 20.2841 10.5556 20.7611 10.8954 21.4089L9.08547 21.9622L9.27084 20.0787Z"
                                                fill="#F5F5F5"
                                            />
                                            <path
                                                d="M13.5 22.875C13.5 22.5298 13.7798 22.25 14.125 22.25H22.875C23.2202 22.25 23.5 22.5298 23.5 22.875C23.5 23.2202 23.2202 23.5 22.875 23.5H14.125C13.7798 23.5 13.5 23.2202 13.5 22.875Z"
                                                fill="#818998"
                                            />
                                        </svg>
                                    }
                                />
                                <UiButton
                                    onClick={() => onSecondModal('delete')}
                                    className=""
                                    variant="icon"
                                    startIcon={
                                        <svg
                                            width="32"
                                            height="32"
                                            viewBox="0 0 32 32"
                                            fill="none"
                                            xmlns="http://www.w3.org/2000/svg"
                                        >
                                            <path
                                                d="M20.1667 24.3333H11.8333C10.9129 24.3333 10.1667 23.5871 10.1667 22.6667V11.8333H8.5V10.1667H11.8333V9.33334C11.8333 8.41286 12.5795 7.66667 13.5 7.66667H18.5C19.4205 7.66667 20.1667 8.41286 20.1667 9.33334V10.1667H23.5V11.8333H21.8333V22.6667C21.8333 23.5871 21.0871 24.3333 20.1667 24.3333ZM11.8333 11.8333V22.6667H20.1667V11.8333H11.8333ZM13.5 9.33334V10.1667H18.5V9.33334H13.5ZM18.5 21H16.8333V13.5H18.5V21ZM15.1667 21H13.5V13.5H15.1667V21Z"
                                                fill="#E04E2A"
                                            />
                                        </svg>
                                    }
                                />
                            </div>
                        </div>
                        {isPrevFormat ? (
                            <div
                                className={classNames(cls.prevFiles, {
                                    [cls.imgPrevFile]: fileIsImg,
                                })}
                                onClick={(event) => event.stopPropagation()}
                            >
                                {fileIsImg ? (
                                    <img
                                        className={cls.imgModal}
                                        src={currentFileInfo.originalUrl}
                                        alt={currentFileInfo.name}
                                    />
                                ) : (
                                    <Frame
                                        // currentFileId={currentFileInfo.id}
                                        decodeURL={decodeURL}
                                        iframeSrc={iframeSrc}
                                    />
                                )}
                            </div>
                        ) : (
                            <div
                                className={classNames(cls.customModalContent)}
                                onClick={(event) => event.stopPropagation()}
                            >
                                <div className={cls.notPrevFormatBody}>
                                    {t('PrevModal.return.customModalContent.notPrevFormatBody')}
                                </div>
                                <div className={cls.notPrevFormatFooter}>
                                    <UiButton
                                        onClick={async () => {
                                            await saveFile(
                                                currentFileInfo.originalUrl,
                                                decodedNameFile
                                            );
                                        }}
                                        className=""
                                        startIcon={
                                            <svg
                                                width="16"
                                                height="16"
                                                viewBox="0 0 16 16"
                                                fill="none"
                                                xmlns="http://www.w3.org/2000/svg"
                                            >
                                                <mask id="path-1-inside-1_207_3513" fill="white">
                                                    <path d="M0.5 9.89999C0.632608 9.89999 0.759785 9.95267 0.853553 10.0464C0.947322 10.1402 1 10.2674 1 10.4V12.9C1 13.1652 1.10536 13.4196 1.29289 13.6071C1.48043 13.7946 1.73478 13.9 2 13.9H14C14.2652 13.9 14.5196 13.7946 14.7071 13.6071C14.8946 13.4196 15 13.1652 15 12.9V10.4C15 10.2674 15.0527 10.1402 15.1464 10.0464C15.2402 9.95267 15.3674 9.89999 15.5 9.89999C15.6326 9.89999 15.7598 9.95267 15.8536 10.0464C15.9473 10.1402 16 10.2674 16 10.4V12.9C16 13.4304 15.7893 13.9391 15.4142 14.3142C15.0391 14.6893 14.5304 14.9 14 14.9H2C1.46957 14.9 0.960859 14.6893 0.585786 14.3142C0.210714 13.9391 0 13.4304 0 12.9V10.4C0 10.2674 0.0526784 10.1402 0.146447 10.0464C0.240215 9.95267 0.367392 9.89999 0.5 9.89999Z" />
                                                </mask>
                                                <path
                                                    d="M0.5 9.89999C0.632608 9.89999 0.759785 9.95267 0.853553 10.0464C0.947322 10.1402 1 10.2674 1 10.4V12.9C1 13.1652 1.10536 13.4196 1.29289 13.6071C1.48043 13.7946 1.73478 13.9 2 13.9H14C14.2652 13.9 14.5196 13.7946 14.7071 13.6071C14.8946 13.4196 15 13.1652 15 12.9V10.4C15 10.2674 15.0527 10.1402 15.1464 10.0464C15.2402 9.95267 15.3674 9.89999 15.5 9.89999C15.6326 9.89999 15.7598 9.95267 15.8536 10.0464C15.9473 10.1402 16 10.2674 16 10.4V12.9C16 13.4304 15.7893 13.9391 15.4142 14.3142C15.0391 14.6893 14.5304 14.9 14 14.9H2C1.46957 14.9 0.960859 14.6893 0.585786 14.3142C0.210714 13.9391 0 13.4304 0 12.9V10.4C0 10.2674 0.0526784 10.1402 0.146447 10.0464C0.240215 9.95267 0.367392 9.89999 0.5 9.89999Z"
                                                    fill="#F5F5F5"
                                                />
                                                <path
                                                    d="M1 10.4H2.5H1ZM1 12.9H-0.5H1ZM0 12.9H-1.5H0ZM0 10.4H-1.5H0ZM0.5 11.4C0.234779 11.4 -0.0195734 11.2946 -0.207107 11.1071L1.91421 8.98578C1.53914 8.61071 1.03044 8.39999 0.5 8.39999V11.4ZM-0.207107 11.1071C-0.394642 10.9196 -0.5 10.6652 -0.5 10.4H2.5C2.5 9.86956 2.28929 9.36085 1.91421 8.98578L-0.207107 11.1071ZM-0.5 10.4V12.9H2.5V10.4H-0.5ZM-0.5 12.9C-0.5 13.563 -0.236609 14.1989 0.232233 14.6678L2.35355 12.5464C2.44732 12.6402 2.5 12.7674 2.5 12.9H-0.5ZM0.232233 14.6678C0.701076 15.1366 1.33696 15.4 2 15.4V12.4C2.13261 12.4 2.25978 12.4527 2.35355 12.5464L0.232233 14.6678ZM2 15.4H14V12.4H2V15.4ZM14 15.4C14.663 15.4 15.2989 15.1366 15.7678 14.6678L13.6464 12.5464C13.7402 12.4527 13.8674 12.4 14 12.4V15.4ZM15.7678 14.6678C16.2366 14.1989 16.5 13.563 16.5 12.9H13.5C13.5 12.7674 13.5527 12.6402 13.6464 12.5464L15.7678 14.6678ZM16.5 12.9V10.4H13.5V12.9H16.5ZM16.5 10.4C16.5 10.6652 16.3946 10.9196 16.2071 11.1071L14.0858 8.98578C13.7107 9.36085 13.5 9.86956 13.5 10.4H16.5ZM16.2071 11.1071C16.0196 11.2946 15.7652 11.4 15.5 11.4V8.39999C14.9696 8.39999 14.4609 8.61071 14.0858 8.98578L16.2071 11.1071ZM15.5 11.4C15.2348 11.4 14.9804 11.2946 14.7929 11.1071L16.9142 8.98578C16.5391 8.61071 16.0304 8.39999 15.5 8.39999V11.4ZM14.7929 11.1071C14.6054 10.9196 14.5 10.6652 14.5 10.4H17.5C17.5 9.86956 17.2893 9.36085 16.9142 8.98578L14.7929 11.1071ZM14.5 10.4V12.9H17.5V10.4H14.5ZM14.5 12.9C14.5 13.0326 14.4473 13.1598 14.3536 13.2535L16.4749 15.3749C17.1313 14.7185 17.5 13.8283 17.5 12.9H14.5ZM14.3536 13.2535C14.2598 13.3473 14.1326 13.4 14 13.4V16.4C14.9283 16.4 15.8185 16.0312 16.4749 15.3749L14.3536 13.2535ZM14 13.4H2V16.4H14V13.4ZM2 13.4C1.86739 13.4 1.74021 13.3473 1.64645 13.2535L-0.474874 15.3749C0.181504 16.0312 1.07174 16.4 2 16.4V13.4ZM1.64645 13.2535C1.55268 13.1598 1.5 13.0326 1.5 12.9H-1.5C-1.5 13.8283 -1.13125 14.7185 -0.474874 15.3749L1.64645 13.2535ZM1.5 12.9V10.4H-1.5V12.9H1.5ZM1.5 10.4C1.5 10.6652 1.39464 10.9196 1.20711 11.1071L-0.914214 8.98578C-1.28928 9.36085 -1.5 9.86956 -1.5 10.4H1.5ZM1.20711 11.1071C1.01957 11.2946 0.765221 11.4 0.5 11.4V8.39999C-0.0304374 8.39999 -0.539144 8.61071 -0.914214 8.98578L1.20711 11.1071Z"
                                                    fill="#F5F5F5"
                                                    mask="url(#path-1-inside-1_207_3513)"
                                                />
                                                <path
                                                    d="M7.64602 11.854C7.69247 11.9006 7.74764 11.9375 7.80839 11.9627C7.86913 11.9879 7.93425 12.0009 8.00002 12.0009C8.06579 12.0009 8.13091 11.9879 8.19165 11.9627C8.2524 11.9375 8.30758 11.9006 8.35402 11.854L11.354 8.854C11.4479 8.76011 11.5007 8.63278 11.5007 8.5C11.5007 8.36722 11.4479 8.23989 11.354 8.146C11.2601 8.05211 11.1328 7.99937 11 7.99937C10.8672 7.99937 10.7399 8.05211 10.646 8.146L8.50002 10.293V1.5C8.50002 1.36739 8.44734 1.24021 8.35358 1.14645C8.25981 1.05268 8.13263 1 8.00002 1C7.86741 1 7.74024 1.05268 7.64647 1.14645C7.5527 1.24021 7.50002 1.36739 7.50002 1.5V10.293L5.35402 8.146C5.26013 8.05211 5.1328 7.99937 5.00002 7.99937C4.86725 7.99937 4.73991 8.05211 4.64602 8.146C4.55213 8.23989 4.49939 8.36722 4.49939 8.5C4.49939 8.63278 4.55213 8.76011 4.64602 8.854L7.64602 11.854Z"
                                                    fill="#F5F5F5"
                                                />
                                            </svg>
                                        }
                                        btnText={t(
                                            'PrevModal.return.notPrevFormatFooter.saveButton.text'
                                        )}
                                    />
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </>
        )
    );
};
